/**
 * Vanilla widget-dom renderer (WIDGET track, investigation §3 v3).
 *
 * The "one UI" of one-UI-two-data-adapters: the framework-free pill — the
 * ONLY implementation of the widget's visuals and gesture mechanics (hold /
 * swipe-cancel / pointer-capture quirks around the shared PTTStateMachine).
 * It consumes the renderer-agnostic seam (`WidgetState` in / `WidgetActions`
 * out via a `StateSource`) and never touches the transport:
 *
 *   - `createWidgetRenderer(container, source, opts)` — the renderer on the
 *     seam. The iframe app hosts THIS with its React data adapter
 *     (components/widget-host/); the SDK path wraps it below.
 *   - `createDealerWidget(container, service, opts)` — SDK entry point:
 *     wraps the renderer around `createSdkStateSource(service)` and wires the
 *     analyser providers for the visualizer.
 */

import {
  resolveLanguage,
  translate,
  type LanguageCode,
  type MessageKey,
} from '../core/i18n/index.js';
import { getLogger } from '../core/logger.js';
import { PTTStateMachine } from '../core/logic/ptt-state.js';
import type { StateSource, WidgetState } from '../core/logic/widget-state.js';
import { ServiceMode } from '../core/types/service-mode.js';
import type { PTTState } from '../core/types/widget-types.js';
import type { BaseSDKService } from '../sdk/BaseSDKService.js';
import { createSdkStateSource } from '../sdk/state-source.js';

import {
  chevronUpIcon,
  closeIcon,
  keyboardIcon,
  microphoneIcon,
  micOffIcon,
  playIcon,
  sendIcon,
  strokeMicIcon,
  warnIcon,
} from './icons.js';
import { ensureWidgetStyles } from './styles.js';
import { createBarVisualizer, type AnalyserProvider, type VisualizerState } from './visualizer.js';

/**
 * Display mode. The host drives the welcome flow (`welcome-teach` /
 * `welcome-cta` / `degraded`) via `setMode` and
 * switches to `live` when the session is on the table; `live` renders from
 * the WidgetState stream.
 */
export type DealerWidgetMode = 'live' | 'welcome-teach' | 'welcome-cta' | 'degraded';

export interface DealerWidgetOptions {
  /** UI language (aliases resolve, falls back to English). */
  language?: string;
  /** Initial display mode. Default 'live'. */
  mode?: DealerWidgetMode;
  /** Animate the dealer's voice in the bar while not holding (default false). */
  showDealerVisualizer?: boolean;
  /**
   * Welcome-CTA tap. The widget burns the gesture on `actions.startAudio()`
   * first (the Safari activation path), then notifies the host to advance
   * its welcome flow.
   */
  onCtaTap?: () => void;
  /** Phase-1 mic-status dwell before it yields the pill back (default 4000ms). */
  micPhaseDelayMs?: number;
  /**
   * Gesture-state notifications (hold / swipe-cancel / in-flight), fired on
   * every change. Hosts forward these to their integration surface — the
   * iframe app's parent `PTT_STATE_CHANGE` events ride this.
   */
  onPTTStateChange?: (state: PTTState) => void;
  /**
   * Composer open/close, fired on every change. The iframe app forwards it as
   * the parent `TEXT_INPUT_STATE` message so the lib can keep the widget above
   * a mobile keyboard (ADR 0065).
   */
  onTextInputChange?: (open: boolean) => void;
}

export interface WidgetRendererOptions extends DealerWidgetOptions {
  /** Local-mic analyser for the holding-state visualizer. */
  getLocalAnalyser?: AnalyserProvider;
  /** Dealer-voice analyser (used with `showDealerVisualizer`). */
  getDealerAnalyser?: AnalyserProvider;
}

export interface WidgetHandle {
  setMode(mode: DealerWidgetMode): void;
  destroy(): void;
  readonly element: HTMLElement;
}

/** Swipe-up distance (px) that cancels the hold. */
const SWIPE_CANCEL_THRESHOLD = 60;
const MIC_PHASE_DELAY_MS = 4000;
/** Cancel-animation timing (HoldToTalkButton: reset at 450ms, overlay gone at ~750ms). */
/**
 * A hold must end. The browser is not obliged to tell us: the mic-permission
 * prompt swallows the release, and nothing else in the DOM reports it, so a
 * press can otherwise stay "held" forever — leaving a turn open at the agent
 * with the mic live for the rest of the session (ADR 0057 rule 4).
 *
 * Generous on purpose, and it ends the hold the way a RELEASE would, never a
 * cancel: if it ever fires on a genuinely long hold, the player's speech is
 * still committed rather than discarded.
 */
const MAX_HOLD_MS = 60_000;
const CANCEL_RESET_MS = 450;
const CANCEL_HIDE_MS = 750;
/** Composer field cap; the agent drops anything above its own, looser cap (ADR 0065). */
const MAX_TEXT_LENGTH = 140;
/** Leaving an empty field closes the composer after this grace (a tap on the button is not leaving). */
const BLUR_GRACE_MS = 120;
/** The thinking dots give the pill back to the last caption if no answer arrives. */
const THINKING_DOTS_MAX_MS = 10_000;

const logger = getLogger('DealerWidget');

type CancelAnimPhase = 'none' | 'pop' | 'fading';
type StatusKey =
  | 'mic-blocked'
  | 'mic-unavailable'
  | 'mic-enabling'
  | 'mic-enabled'
  | 'degraded'
  | null;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  return node;
}

function show(node: HTMLElement, visible: boolean): void {
  // Inline display beats the stylesheet's flex/absolute rules, unlike [hidden].
  node.style.display = visible ? '' : 'none';
}

export function createWidgetRenderer(
  container: HTMLElement,
  source: StateSource,
  opts: WidgetRendererOptions = {}
): WidgetHandle {
  ensureWidgetStyles(container.ownerDocument);

  const lang: LanguageCode = resolveLanguage(opts.language ?? 'en');
  const t = (key: MessageKey): string => translate(lang, key);
  const micPhaseDelayMs = opts.micPhaseDelayMs ?? MIC_PHASE_DELAY_MS;
  const getLocalAnalyser: AnalyserProvider = opts.getLocalAnalyser ?? (() => null);
  const getDealerAnalyser: AnalyserProvider = opts.getDealerAnalyser ?? (() => null);

  // ---- DOM (built once; render() only toggles classes/visibility/text) ----
  const root = el('div', 'compact-voice-widget');
  root.lang = lang;

  const startAudioButton = el('button', 'compact-start-audio lk-start-audio-button');
  startAudioButton.type = 'button';
  startAudioButton.textContent = t('tapToEnableAudio');
  root.appendChild(startAudioButton);

  const content = el('div', 'compact-voice-widget-content');
  root.appendChild(content);

  const ctaBar = el('button', 'welcome-cta-bar');
  ctaBar.type = 'button';
  ctaBar.innerHTML = playIcon();
  const ctaLabel = document.createElement('span');
  ctaLabel.textContent = t('tapToStart');
  ctaBar.appendChild(ctaLabel);
  content.appendChild(ctaBar);

  const bar = el('div', 'compact-transcription-bar');
  content.appendChild(bar);

  const textWrap = el('div', 'compact-transcription-text');
  bar.appendChild(textWrap);
  const statusLine = el('p', 'transcription-message transcription-status');
  const messageLine = el('p', 'transcription-message');
  const placeholderLine = el('p', 'transcription-placeholder');
  placeholderLine.textContent = t('settingUpTable');
  // Composer (ADR 0065): tapping the pill turns it into a message field.
  const field = el('textarea', 'tx-field');
  field.rows = 1;
  field.maxLength = MAX_TEXT_LENGTH;
  field.placeholder = t('messagePlaceholder');
  field.setAttribute('aria-label', t('messageDealer'));
  field.setAttribute('enterkeyhint', 'send');
  field.setAttribute('autocapitalize', 'sentences');
  field.setAttribute('autocomplete', 'off');
  field.dataset.testid = 'aq-text-field';
  const dots = el('div', 'tx-dots');
  dots.innerHTML = '<i></i><i></i><i></i>';
  dots.setAttribute('role', 'status');
  dots.setAttribute('aria-label', t('dealerThinking'));
  textWrap.append(statusLine, messageLine, placeholderLine, field, dots);

  const vizWrap = el('div', 'compact-visualizer');
  bar.appendChild(vizWrap);
  const visualizer = createBarVisualizer({ barCount: 24, minHeight: 8 });
  vizWrap.appendChild(visualizer.element);

  const glyph = el('span', 'tx-glyph');
  glyph.innerHTML = keyboardIcon(18);
  glyph.setAttribute('aria-hidden', 'true');
  bar.appendChild(glyph);

  const holdContainer = el('div', 'hold-to-talk-container');
  content.appendChild(holdContainer);

  const chevrons = el('div', 'cancel-chevrons');
  chevrons.innerHTML = chevronUpIcon(16) + chevronUpIcon(16);
  const cancelText = el('div', 'cancel-text');
  cancelText.textContent = t('cancelHint');
  const canceledOverlay = el('div', 'hold-to-talk-canceled');
  const canceledIcon = el('div', 'canceled-icon');
  canceledIcon.innerHTML = closeIcon(24);
  canceledOverlay.appendChild(canceledIcon);

  const button = el('button', 'hold-to-talk-button');
  button.type = 'button';
  button.draggable = false;
  button.style.touchAction = 'none';
  button.dataset.testid = 'aq-talk-button';
  holdContainer.append(chevrons, cancelText, canceledOverlay, button);

  const micCaption = el('div', 'mic-status-caption');
  micCaption.innerHTML = micOffIcon(14);
  const micCaptionText = document.createElement('span');
  micCaption.appendChild(micCaptionText);
  root.appendChild(micCaption);

  const errorLine = el('div', 'compact-voice-widget-error');
  const errorText = document.createElement('span');
  errorLine.appendChild(errorText);
  root.appendChild(errorLine);

  const overlay = el('div', 'compact-voice-widget-overlay');
  const overlayText = document.createElement('span');
  overlay.appendChild(overlayText);
  root.appendChild(overlay);

  container.appendChild(root);

  // ---- State ----
  let mode: DealerWidgetMode = opts.mode ?? 'live';
  let state: WidgetState = source.getState();
  // Invisible re-entry guard while PTT actions are in flight. Deliberately
  // NO spinner/icon change on press (the React widget's isLoading spinner
  // flashed on every fast round-trip): the .active glow is the press
  // feedback, WhatsApp-style; the icon never changes during a gesture.
  //
  // A COUNT, not a flag: the writers overlap — a release's endPTT resolves
  // while the press's startPTT is still suspended on the permission prompt —
  // and one writer's finally must not clear a guard another still owns (#467).
  let inFlight = 0;
  const isBusy = (): boolean => inFlight > 0;
  let rpcError: string | null = null;
  let micPhase: 1 | 2 = 1;
  let micPhaseTimer: ReturnType<typeof setTimeout> | null = null;
  let cancelAnim: CancelAnimPhase = 'none';
  let holdDeadlineTimer: ReturnType<typeof setTimeout> | null = null;
  let cancelResetTimer: ReturnType<typeof setTimeout> | null = null;
  let cancelHideTimer: ReturnType<typeof setTimeout> | null = null;
  // Debounced audio-blocked affordance: LiveKit reports canPlaybackAudio
  // false for the first moments of every connect, so revealing immediately
  // flashes the tap-to-enable button on browsers that then grant autoplay.
  // Reveal only when blocked PERSISTS; hide instantly on unblock.
  let audioBlockedShown = false;
  let audioBlockedTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;

  // Rendered-content caches so render() stays cheap and idempotent.
  let renderedStatusKey: StatusKey = null;
  let renderedIconKey = '';
  // Investigation instrumentation: some browsers dispatch NO pointer events on
  // a disabled button, so a dead press there logs nothing — log the transition
  // into/out of the disabled state instead.
  let loggedDisabled: boolean | null = null;

  // ---- PTT gestures ----
  let isActive = false;
  let activePointerId: number | null = null;
  let startY = 0;
  // A browser mic-permission prompt can only appear until the mic has been
  // acquired once (`state.micAcquired`, owned by the state source — a dropped
  // re-entrant startPTT resolves without acquiring, so the renderer never
  // infers it locally). Swipe-cancel is off for that ENTIRE prompt-capable
  // hold (decided once at press time, never mid-gesture): browsers deliver
  // events unpredictably around the prompt (starved, deferred, or reordered
  // against the grant), so any mid-hold re-enable inherits a stale swipe
  // origin and misfires (#458). Safari — where this bug was caught — cannot
  // report microphone permission through the Permissions API
  // (queryMicPermission collapses to 'unsupported' there), so "have we
  // succeeded yet" is the only signal that works on the browser that needs it.
  let swipeCancelDisabled = false;
  let contextMenuBlocker: ((event: Event) => void) | null = null;

  // ---- Text input (ADR 0065) ----
  let composing = false;
  let sending = false;
  let blurTimer: ReturnType<typeof setTimeout> | null = null;
  // Armed (timer pending) by a text send: the pill shows the thinking dots
  // until her next caption, a failed send or the fallback. Not keyed on
  // agentState: our turns never enter the framework's reply pipeline, the only
  // writer of 'thinking' (ADR 0065).
  let awaitingTimer: ReturnType<typeof setTimeout> | null = null;

  const machine = new PTTStateMachine({
    swipeCancelThreshold: SWIPE_CANCEL_THRESHOLD,
    onAccept: () => {
      void runPTTAction(() => source.actions.endPTT());
    },
    onCancel: () => {
      cancelAnim = 'pop';
      void runPTTAction(() => source.actions.cancelPTT());
      cancelResetTimer = setTimeout(() => {
        cancelResetTimer = null;
        machine.reset();
        cancelAnim = 'fading';
        render();
      }, CANCEL_RESET_MS);
      cancelHideTimer = setTimeout(() => {
        cancelHideTimer = null;
        cancelAnim = 'none';
        render();
      }, CANCEL_HIDE_MS);
    },
  });

  function clearCancelAnimation(): void {
    if (cancelResetTimer) clearTimeout(cancelResetTimer);
    if (cancelHideTimer) clearTimeout(cancelHideTimer);
    cancelResetTimer = null;
    cancelHideTimer = null;
  }

  function addContextMenuBlocker(): void {
    if (contextMenuBlocker) return;
    contextMenuBlocker = (event: Event) => event.preventDefault();
    document.addEventListener('contextmenu', contextMenuBlocker, { capture: true });
  }

  function removeContextMenuBlocker(): void {
    if (!contextMenuBlocker) return;
    document.removeEventListener('contextmenu', contextMenuBlocker, { capture: true });
    contextMenuBlocker = null;
  }

  async function runPTTAction(action: () => Promise<void>): Promise<void> {
    inFlight += 1;
    render();
    try {
      await action();
    } catch (err) {
      rpcError = err instanceof Error ? err.message : String(err);
    } finally {
      inFlight -= 1;
      if (!destroyed) render();
    }
  }

  async function startTurn(): Promise<void> {
    rpcError = null;
    inFlight += 1;
    render();
    try {
      // Acquires the mic on first hold (the permission prompt rides this
      // gesture) and rejects when unavailable — micStatus carries the
      // classification, so a mic rejection is guidance, not an error line.
      await source.actions.startPTT();
    } catch (err) {
      isActive = false;
      activePointerId = null;
      removeContextMenuBlocker();
      machine.reset();
      if (source.getState().micStatus === 'ok') {
        // Acquisition succeeded and the turn RPC failed (the state source owns
        // the grant-survives-failure bookkeeping) — show the error line.
        rpcError = err instanceof Error ? err.message : String(err);
      }
    } finally {
      inFlight -= 1;
      if (!destroyed) render();
    }
  }

  function clearHoldDeadline(): void {
    if (holdDeadlineTimer) clearTimeout(holdDeadlineTimer);
    holdDeadlineTimer = null;
  }

  /** True while the browser still routes this pointer to us — the UA drops
   * capture on the real up/cancel, so capture-held implies the gesture is
   * genuinely alive. Best-effort: unsupported or leaked capture just means the
   * deadline does the work instead. */
  function pointerStillCaptured(): boolean {
    if (activePointerId === null) return false;
    try {
      return button.hasPointerCapture?.(activePointerId) ?? true;
    } catch {
      return true;
    }
  }

  /** The release never arrived. End the hold exactly as a release would, so the
   * turn is closed and the mic handed back. */
  function endStrandedHold(reason: string): void {
    logger.debug('hold ended without a release', { reason, activePointerId });
    isActive = false;
    activePointerId = null;
    removeContextMenuBlocker();
    clearHoldDeadline();
    if (machine.getState().isHolding) {
      endHold();
    } else {
      render();
    }
  }

  function endHold(): void {
    clearHoldDeadline();
    machine.endHold();
    render();
  }

  function abortHold(): void {
    isActive = false;
    activePointerId = null;
    removeContextMenuBlocker();
    clearHoldDeadline();
    if (machine.getState().isHolding) {
      endHold();
    }
  }

  function isAgentReady(): boolean {
    return state.agentState !== 'disconnected';
  }

  function isPausedOrSuspended(): boolean {
    return state.mode === ServiceMode.Paused || state.suspended;
  }

  function canCompose(): boolean {
    return (
      mode === 'live' &&
      !isPausedOrSuspended() &&
      isAgentReady() &&
      !machine.getState().isHolding
    );
  }

  /** The draft as it would be sent: one line, trimmed, capped. */
  function draftText(): string {
    return field.value.replace(/\s+/g, ' ').trim().slice(0, MAX_TEXT_LENGTH);
  }

  function clearBlurTimer(): void {
    if (blurTimer) clearTimeout(blurTimer);
    blurTimer = null;
  }

  /** Every close drops the draft; only leaving the field keeps it (no close). */
  function closeComposer(): void {
    if (!composing) return;
    composing = false;
    field.value = '';
    field.blur();
    // After blur(): its listener has just scheduled a grace timer a close does not need.
    clearBlurTimer();
    render();
    opts.onTextInputChange?.(false);
  }

  function lastCaptionId(s: WidgetState): string | undefined {
    return s.captions[s.captions.length - 1]?.segmentId;
  }

  function disarmAwaitingReply(): void {
    if (awaitingTimer) clearTimeout(awaitingTimer);
    awaitingTimer = null;
  }

  function armAwaitingReply(): void {
    disarmAwaitingReply();
    awaitingTimer = setTimeout(() => {
      awaitingTimer = null;
      render();
    }, THINKING_DOTS_MAX_MS);
  }

  async function sendDraft(): Promise<void> {
    const text = draftText();
    // One send in flight at a time; a draft typed meanwhile waits for it.
    if (text === '' || sending) return;
    sending = true;
    rpcError = null;
    closeComposer();
    armAwaitingReply();
    try {
      await source.actions.sendText(text);
    } catch (err) {
      disarmAwaitingReply();
      rpcError = err instanceof Error ? err.message : String(err);
    } finally {
      sending = false;
      if (!destroyed) render();
    }
  }

  const onPointerDown = (event: Event): void => {
    const e = event as PointerEvent;
    if (mode !== 'live') {
      logger.debug('press ignored: mode', mode);
      return;
    }
    if (composing) {
      // The field keeps focus: a press on the button is not leaving it.
      e.preventDefault();
      if (draftText() !== '') {
        void sendDraft();
        return;
      }
      // Empty composer: the press closes it and becomes an ordinary hold.
      closeComposer();
    }
    // A hold we still believe in, whose pointer the browser has already let go,
    // is a gesture whose release was never delivered. Reclaim it here rather
    // than refusing every press behind it (ADR 0057 rule 4).
    if (isActive && !pointerStillCaptured()) {
      endStrandedHold('a new press arrived while the previous one had no pointer');
    }
    if (isPausedOrSuspended() || !isAgentReady() || isBusy() || isActive) {
      logger.debug('press ignored:', {
        paused: isPausedOrSuspended(),
        agentReady: isAgentReady(),
        busy: isBusy(),
        isActive,
      });
      return;
    }
    e.preventDefault();
    try {
      button.setPointerCapture?.(e.pointerId);
    } catch {
      /* jsdom / synthetic pointers have no capture */
    }
    activePointerId = e.pointerId;
    startY = e.clientY;
    isActive = true;
    // Decided once per gesture, never mid-hold (see the declaration comment).
    swipeCancelDisabled = !state.micAcquired;
    addContextMenuBlocker();
    // The previous cancel's reset timer would otherwise fire mid-gesture and
    // wipe THIS hold, leaking its turn at the agent (#458).
    clearCancelAnimation();
    cancelAnim = 'none';
    machine.startHold();
    clearHoldDeadline();
    holdDeadlineTimer = setTimeout(() => {
      holdDeadlineTimer = null;
      endStrandedHold(`no release within ${MAX_HOLD_MS}ms`);
    }, MAX_HOLD_MS);
    render();
    void startTurn();
  };

  const onPointerMove = (event: Event): void => {
    const e = event as PointerEvent;
    if (!isActive || !machine.getState().isHolding) return;
    if (activePointerId !== null && e.pointerId !== activePointerId) return;
    if (swipeCancelDisabled) {
      // The prompt-capable hold has no swipe-cancel at all: travelling to the
      // permission prompt is not a swipe, and event delivery around the prompt
      // is unreliable enough that any mid-hold re-enable inherits a stale
      // origin and misfires (#458). The cancel affordances are hidden for this
      // hold, so nothing advertised is being ignored.
      return;
    }
    const deltaY = startY - e.clientY;
    machine.updateSwipe(deltaY);
    if (deltaY >= SWIPE_CANCEL_THRESHOLD) {
      isActive = false;
      activePointerId = null;
      removeContextMenuBlocker();
    }
    render();
  };

  const onPointerUp = (event: Event): void => {
    const e = event as PointerEvent;
    removeContextMenuBlocker();
    try {
      button.releasePointerCapture?.(e.pointerId);
    } catch {
      /* not captured */
    }
    if (activePointerId !== null && e.pointerId !== activePointerId) return;
    const snapshot = machine.getState();
    if (!isActive || !snapshot.isHolding) return;
    isActive = false;
    activePointerId = null;
    if (snapshot.swipeProgress < 1) {
      endHold();
    }
  };

  const onPointerCancel = (event: Event): void => {
    const e = event as PointerEvent;
    removeContextMenuBlocker();
    try {
      button.releasePointerCapture?.(e.pointerId);
    } catch {
      /* not captured */
    }
    if (activePointerId !== null && e.pointerId !== activePointerId) return;
    if (!isActive) return;
    isActive = false;
    activePointerId = null;
    if (machine.getState().isHolding) {
      endHold();
    }
  };

  const onPointerLeave = (): void => {
    if (!isActive) return;
    // Firefox (bugzilla 1666851) fires pointerleave during active pointer
    // capture; if capture is still held the pointer hasn't really left.
    if (activePointerId !== null && button.hasPointerCapture?.(activePointerId)) {
      return;
    }
    abortHold();
  };

  const preventContextMenu = (event: Event): void => event.preventDefault();

  // Window-level fallbacks: a hold must never survive losing the pointer/tab.
  const onWindowPointerUp = (event: PointerEvent): void => {
    if (!isActive) return;
    if (activePointerId !== null && event.pointerId !== activePointerId) return;
    isActive = false;
    activePointerId = null;
    removeContextMenuBlocker();
    if (machine.getState().swipeProgress < 1) {
      endHold();
    }
  };
  const onWindowPointerCancel = (event: PointerEvent): void => {
    if (!isActive) return;
    if (activePointerId !== null && event.pointerId !== activePointerId) return;
    abortHold();
  };
  const onWindowBlur = (): void => {
    if (!isActive) return;
    abortHold();
  };
  const onVisibilityChange = (): void => {
    if (!document.hidden || !isActive) return;
    abortHold();
  };

  // Investigation instrumentation (#458 follow-up): every press landing
  // anywhere on the widget logs a full state snapshot at debug level. A press
  // eaten by pointer-events:none (.loading/.paused/.audio-blocked) never
  // reaches the button handler but still hit-tests to an ancestor, so this
  // capture listener sees it — a swallowed press names its guard instead of
  // vanishing without a trace.
  const logPressSnapshot = (event: Event): void => {
    const e = event as PointerEvent;
    const target = e.target as HTMLElement | null;
    const snapshot = machine.getState();
    logger.debug('press', {
      type: e.type,
      target: target?.className || target?.tagName || '?',
      onButton: target === button || (target !== null && button.contains(target)),
      pointerId: e.pointerId,
      hasFocus: root.ownerDocument.hasFocus(),
      mode,
      busy: isBusy(),
      inFlight,
      isActive,
      activePointerId,
      holding: snapshot.isHolding,
      canceled: snapshot.isCanceled,
      disabled: button.disabled,
      agentState: state.agentState,
      paused: isPausedOrSuspended(),
      audioBlockedShown,
      micStatus: state.micStatus,
      micAcquired: state.micAcquired,
      swipeCancelDisabled,
    });
  };
  root.addEventListener('pointerdown', logPressSnapshot, { capture: true });

  button.addEventListener('pointerdown', onPointerDown);
  button.addEventListener('pointermove', onPointerMove);
  button.addEventListener('pointerup', onPointerUp);
  button.addEventListener('pointercancel', onPointerCancel);
  button.addEventListener('pointerleave', onPointerLeave);
  button.addEventListener('contextmenu', preventContextMenu);
  holdContainer.addEventListener('contextmenu', preventContextMenu, { capture: true });
  window.addEventListener('pointerup', onWindowPointerUp);
  window.addEventListener('pointercancel', onWindowPointerCancel);
  window.addEventListener('blur', onWindowBlur);
  document.addEventListener('visibilitychange', onVisibilityChange);

  // Only a host that is the top page sees the keyboard and pins us above it; in a
  // casino frame the browser must scroll the field into view itself (#557).
  const hostIsTopPage = window.parent === window.top;
  bar.addEventListener('click', () => {
    if (composing || !canCompose()) return;
    composing = true;
    render();
    // Inside the tap, synchronously: iOS raises the keyboard only for a
    // focus() that runs in the user gesture. preventScroll stops iOS panning
    // the whole host page to reveal the field; the lib pins us above the
    // keyboard instead (#557 CP5).
    field.focus({ preventScroll: hostIsTopPage });
    opts.onTextInputChange?.(true);
  });
  // A tap straight on the field while it is not focused (a draft kept after the
  // keyboard went down) would focus it natively and iOS would pan the page:
  // take the tap and focus without the scroll, as the pill tap does.
  field.addEventListener('touchend', (e: TouchEvent) => {
    if (root.ownerDocument.activeElement === field) return; // caret taps stay native
    e.preventDefault();
    field.focus({ preventScroll: hostIsTopPage });
  });
  field.addEventListener('input', () => render());
  field.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.isComposing) return; // an IME's Enter confirms the candidate, it does not send
    if (e.key === 'Enter') {
      e.preventDefault(); // no newlines; Shift+Enter does nothing
      if (!e.shiftKey) void sendDraft();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeComposer();
    }
  });
  field.addEventListener('blur', () => {
    clearBlurTimer();
    blurTimer = setTimeout(() => {
      blurTimer = null;
      // A draft survives leaving the field; an empty field closes.
      if (composing && root.ownerDocument.activeElement !== field && draftText() === '') {
        closeComposer();
      }
    }, BLUR_GRACE_MS);
  });

  startAudioButton.addEventListener('click', () => {
    // Called synchronously in the gesture — Safari's activation requirement.
    void source.actions.startAudio().catch(() => {
      /* stays blocked; the button remains until audioPlaybackChanged clears it */
    });
  });

  ctaBar.addEventListener('click', () => {
    void source.actions.startAudio().catch(() => {
      /* pre-connect welcome: nothing to unblock yet; the host advances the flow */
    });
    opts.onCtaTap?.();
  });

  // ---- Mic two-phase (pill message, then persistent bottom-band caption) ----
  function micNeedsAttention(s: WidgetState): boolean {
    return s.micStatus === 'blocked' || s.micStatus === 'unavailable';
  }

  const AUDIO_BLOCKED_REVEAL_MS = 600;

  function syncAudioBlocked(blocked: boolean): void {
    if (!blocked) {
      if (audioBlockedTimer) {
        clearTimeout(audioBlockedTimer);
        audioBlockedTimer = null;
      }
      audioBlockedShown = false;
      return;
    }
    if (audioBlockedShown || audioBlockedTimer) return;
    audioBlockedTimer = setTimeout(() => {
      audioBlockedTimer = null;
      audioBlockedShown = true;
      render();
    }, AUDIO_BLOCKED_REVEAL_MS);
  }

  function resetMicPhase(): void {
    if (micPhaseTimer) {
      clearTimeout(micPhaseTimer);
      micPhaseTimer = null;
    }
    micPhase = 1;
    if (micNeedsAttention(state)) {
      micPhaseTimer = setTimeout(() => {
        micPhase = 2;
        render();
      }, micPhaseDelayMs);
    }
  }

  // ---- Rendering ----
  function setStatusContent(key: StatusKey): void {
    if (key === renderedStatusKey) return;
    renderedStatusKey = key;
    if (key === null) return;
    const icon =
      key === 'degraded'
        ? warnIcon(15)
        : key === 'mic-enabled' || key === 'mic-enabling'
          ? microphoneIcon(15)
          : micOffIcon(15);
    const text =
      key === 'degraded'
        ? t('aiUnavailable')
        : key === 'mic-blocked'
          ? t('micEnableInSettings')
          : key === 'mic-enabling'
            ? t('micEnabling')
            : key === 'mic-enabled'
              ? t('micEnabledHoldToTalk')
              : t('micUnavailable');
    statusLine.innerHTML = icon;
    const span = document.createElement('span');
    span.textContent = text;
    statusLine.appendChild(span);
  }

  function setButtonIcon(key: string, html: string): void {
    if (key === renderedIconKey) return;
    renderedIconKey = key;
    button.innerHTML = html;
  }

  function renderWelcome(): void {
    const degraded = mode === 'degraded';
    root.className = `compact-voice-widget${degraded ? ' degraded' : ''}`;
    show(startAudioButton, false);
    show(micCaption, false);
    show(errorLine, false);
    show(overlay, false);
    show(chevrons, false);
    show(cancelText, false);
    show(canceledOverlay, false);
    show(vizWrap, false);
    visualizer.update(null);

    const cta = mode === 'welcome-cta';
    show(ctaBar, cta);
    show(bar, !cta);
    bar.className = 'compact-transcription-bar';
    show(textWrap, true);
    show(statusLine, degraded);
    show(messageLine, false);
    show(placeholderLine, !degraded);
    show(field, false);
    show(dots, false);
    show(glyph, false);
    textWrap.classList.remove('tx-has-glyph');
    if (degraded) setStatusContent('degraded');

    button.className = `hold-to-talk-button${degraded ? '' : ' welcome-cue'}`;
    button.disabled = degraded;
    if (degraded) {
      button.removeAttribute('aria-hidden');
      button.removeAttribute('tabindex');
    } else {
      // Decorative teaching cue only — not actionable in the welcome modes.
      button.setAttribute('aria-hidden', 'true');
      button.tabIndex = -1;
    }
    setButtonIcon('stroke-mic', strokeMicIcon(24));
  }

  function renderLive(): void {
    const snapshot = machine.getState();
    const holding = snapshot.isHolding;
    const pausedOrSuspended = isPausedOrSuspended();
    const needsAttention = micNeedsAttention(state);

    // No unlock offer while there is nothing to unlock: with the agent absent
    // (transport dropped, automatic reconnect in flight — #478) startAudio has
    // no connected room to act on, so a tap would only waste its gesture. The
    // pill returns once the agent is back and playback is still blocked.
    const offerAudioUnlock = audioBlockedShown && isAgentReady();
    root.className =
      'compact-voice-widget' +
      (pausedOrSuspended ? ' paused' : '') +
      (offerAudioUnlock ? ' audio-blocked' : '');

    show(startAudioButton, offerAudioUnlock);
    show(ctaBar, false);
    show(bar, true);

    bar.className =
      'compact-transcription-bar' +
      (holding ? ' visualizer-only active' : '') +
      (composing ? ' tx-compose' : canCompose() ? ' tx-openable' : '');

    // Pill text: phase-1 mic status > acquiring > grant hint > caption >
    // placeholder (unless suspended). "Acquiring" is derived, not stored: an
    // in-flight action before the session's first acquisition IS the permission
    // hand-off, and the button is already non-pressable for it (.loading).
    const micAcquiring = isBusy() && !state.micAcquired;
    const statusKey: StatusKey =
      needsAttention && micPhase === 1
        ? state.micStatus === 'blocked'
          ? 'mic-blocked'
          : 'mic-unavailable'
        : micAcquiring
          ? 'mic-enabling'
          : state.micJustEnabled
            ? 'mic-enabled'
            : null;
    const lastCaption = state.captions[state.captions.length - 1];
    const message = state.suspended ? '' : (lastCaption?.text ?? '');
    // The composer wins the pill, then the thinking dots, then the chain above.
    const showDots = !composing && awaitingTimer !== null;
    const textLines = !composing && !showDots;
    show(textWrap, !holding);
    show(field, composing);
    show(dots, showDots);
    show(statusLine, textLines && statusKey !== null);
    show(messageLine, textLines && statusKey === null && message !== '');
    show(placeholderLine, textLines && statusKey === null && message === '' && !state.suspended);
    if (statusKey) setStatusContent(statusKey);
    if (messageLine.textContent !== message) messageLine.textContent = message;

    const showGlyph = canCompose() && !composing && !showDots;
    show(glyph, showGlyph);
    textWrap.classList.toggle('tx-has-glyph', showGlyph);

    // Visualizer: local mic while holding; dealer voice when enabled.
    const showViz = holding || opts.showDealerVisualizer === true;
    show(vizWrap, showViz);
    vizWrap.className = 'compact-visualizer' + (holding ? ' expanded' : '');
    if (holding) {
      visualizer.update('speaking', getLocalAnalyser);
    } else if (opts.showDealerVisualizer) {
      visualizer.update(state.agentState as VisualizerState, getDealerAnalyser);
    } else {
      visualizer.update(null);
    }

    // Cancel affordances (chevrons above, hint below) while holding — hidden
    // on the prompt-capable hold, where swipe-cancel is off (#458).
    const showCancelHint = holding && !snapshot.isCanceled && !swipeCancelDisabled;
    show(chevrons, showCancelHint);
    show(cancelText, showCancelHint);
    if (showCancelHint) {
      const progress = snapshot.swipeProgress;
      const opacity = String(0.4 + progress * 0.6);
      chevrons.style.opacity = opacity;
      chevrons.style.transform = `translateY(${-progress * 6}px)`;
      cancelText.style.opacity = opacity;
      const near = progress > 0.5;
      chevrons.classList.toggle('near-cancel', near);
      cancelText.classList.toggle('near-cancel', near);
    }

    show(canceledOverlay, cancelAnim !== 'none');
    canceledIcon.classList.toggle('fading', cancelAnim === 'fading');

    // While the composer holds a draft the button is the send arrow.
    const sendReady = composing && draftText() !== '';
    button.className =
      'hold-to-talk-button' +
      (holding ? ' active' : '') +
      // .loading only disables pointer-events — the invisible re-entry guard.
      // A press never changes the icon (no spinner, no per-press innerHTML):
      // the .active glow + lime icon color are the whole press feedback.
      (isBusy() ? ' loading' : '') +
      (snapshot.isCanceled ? ' canceled' : '') +
      (sendReady ? ' tx-send' : needsAttention ? ' mic-off' : '');
    button.disabled = pausedOrSuspended || !isAgentReady();
    if (button.disabled !== loggedDisabled) {
      loggedDisabled = button.disabled;
      logger.debug('button', button.disabled ? 'disabled' : 'enabled', {
        paused: pausedOrSuspended,
        agentState: state.agentState,
      });
    }
    button.removeAttribute('aria-hidden');
    button.removeAttribute('tabindex');
    if (sendReady) {
      button.setAttribute('aria-label', t('sendMessage'));
      setButtonIcon('send', sendIcon(24));
    } else {
      button.removeAttribute('aria-label');
      setButtonIcon(
        needsAttention ? 'mic-off' : 'mic',
        needsAttention ? micOffIcon(24) : microphoneIcon(24)
      );
    }

    // Phase-2 persistent caption ('blocked' only — 'unavailable' has nothing to enable).
    const showMicCaption = state.micStatus === 'blocked' && micPhase === 2;
    show(micCaption, showMicCaption);
    if (showMicCaption) micCaptionText.textContent = t('micEnableInSettings');

    show(errorLine, rpcError !== null);
    if (rpcError !== null) errorText.textContent = rpcError;

    show(overlay, pausedOrSuspended);
    if (pausedOrSuspended) {
      overlayText.textContent =
        state.mode === ServiceMode.Paused ? t('dealerAway') : t('placeBetToTalk');
    }
  }

  // Last-notified gesture state; render() diffs against it so hosts hear
  // every transition (including swipe progress) exactly once.
  let notifiedPTT: PTTState | null = null;

  function notifyPTTState(): void {
    if (!opts.onPTTStateChange) return;
    const snapshot = machine.getState();
    const next: PTTState = {
      isHolding: snapshot.isHolding,
      isCanceled: snapshot.isCanceled,
      isLoading: isBusy(),
      swipeProgress: snapshot.swipeProgress,
    };
    if (
      notifiedPTT !== null &&
      notifiedPTT.isHolding === next.isHolding &&
      notifiedPTT.isCanceled === next.isCanceled &&
      notifiedPTT.isLoading === next.isLoading &&
      notifiedPTT.swipeProgress === next.swipeProgress
    ) {
      return;
    }
    notifiedPTT = next;
    opts.onPTTStateChange(next);
  }

  function render(): void {
    if (destroyed) return;
    if (mode === 'live') {
      renderLive();
    } else {
      renderWelcome();
    }
    notifyPTTState();
  }

  const unsubscribe = source.subscribe((next) => {
    const micStatusChanged = next.micStatus !== state.micStatus;
    const audioBlockedChanged = next.audioBlocked !== state.audioBlocked;
    // A new caption is the dealer's answer; an interim update of the same line is not.
    const captionChanged = lastCaptionId(next) !== lastCaptionId(state);
    state = next;
    if (micStatusChanged) resetMicPhase();
    if (audioBlockedChanged) syncAudioBlocked(next.audioBlocked);
    if (captionChanged) disarmAwaitingReply();
    // The dealer is gone or the table closed the conversation: drop the draft.
    if (composing && !canCompose()) closeComposer();
    render();
  });

  resetMicPhase();
  syncAudioBlocked(state.audioBlocked);
  render();

  return {
    element: root,
    setMode(next: DealerWidgetMode): void {
      if (next === mode) return;
      if (mode === 'live') {
        closeComposer();
        disarmAwaitingReply();
        // Leaving live mid-hold: drop the gesture and release the turn.
        const wasHolding = machine.getState().isHolding;
        isActive = false;
        activePointerId = null;
        removeContextMenuBlocker();
        clearHoldDeadline();
        machine.reset();
        if (wasHolding) {
          void source.actions.cancelPTT().catch(() => {
            /* turn is gone either way */
          });
        }
      }
      mode = next;
      render();
    },
    destroy(): void {
      if (destroyed) return;
      // A host pinned for an open composer must hear it close.
      if (composing) opts.onTextInputChange?.(false);
      destroyed = true;
      unsubscribe();
      visualizer.destroy();
      if (micPhaseTimer) clearTimeout(micPhaseTimer);
      clearHoldDeadline();
      clearCancelAnimation();
      clearBlurTimer();
      disarmAwaitingReply();
      if (audioBlockedTimer) clearTimeout(audioBlockedTimer);
      removeContextMenuBlocker();
      window.removeEventListener('pointerup', onWindowPointerUp);
      window.removeEventListener('pointercancel', onWindowPointerCancel);
      window.removeEventListener('blur', onWindowBlur);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      root.remove();
    },
  };
}

/**
 * Mount the vanilla dealer widget on a connected (or connecting) SDK service.
 * The widget owns its DOM inside `container`; the host owns the service
 * lifecycle (`destroy()` here detaches without disconnecting the service).
 *
 * Dealer-voice playback is the SDK's own `DealerAudio` attach path
 * (config.audioContainer) — the widget adds no audio element.
 */
export function createDealerWidget(
  container: HTMLElement,
  service: BaseSDKService,
  opts: DealerWidgetOptions = {}
): WidgetHandle {
  const source = createSdkStateSource(service);
  const renderer = createWidgetRenderer(container, source, {
    ...opts,
    getLocalAnalyser: () => service.getLocalAudioAnalyser({ fftSize: 2048 })?.analyser ?? null,
    getDealerAnalyser: () => service.getAudioAnalyser({ fftSize: 2048 })?.analyser ?? null,
  });
  return {
    element: renderer.element,
    setMode: renderer.setMode,
    destroy(): void {
      renderer.destroy();
      source.destroy();
    },
  };
}
